import * as React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import { PromotionTile } from '@src/stories/ecommerce/PromotionalTile/PromotionTile';
import { CollectionSidebar } from './CollectionSidebar';
import { CollectionToolbar } from './CollectionToolbar';
import { LoadingSpinner } from '@arctheme-components/elements/LoadingSpinner/LoadingSpinner';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import { getSearchProvider } from '@src/providers/Search';
import { useSearch } from '@src/contexts/SearchProvider';
import { useSearchSettings } from '@src/contexts/SearchSettingsContext';
import { CollectionProvider, useCollection } from '@src/contexts/CollectionContext';
import ProductListPagination from '@src/stories/ecommerce/Product/ProductListPagination';
import Preloader from '@src/stories/client/Preloader/Preloader';
import { useResponsiveBreakpoints, getGlobalBreakpoints } from '@src/hooks/useMediaQuery';

import './Collection.scss';

// Helper to calculate promo blocks for a given page
// Now supports both column spans and row spans (1-2 rows)
function getPromoCountForPage(
  promoBlocks: any[],
  columns: any,
  paginationType: string = 'pages'
): number {

  // For load more pagination, promotions don't take up product slots
  // They get slotted in alongside products as additional items
  if (paginationType === 'load_more') {
    return 0;
  }

  // Flatten all promo positions
  const flattenedPromos = promoBlocks.flatMap((promo) => {
    if (Array.isArray(promo.position)) {
      return promo.position.map((pos) => ({ ...promo, position: pos }));
    }
    return [promo];
  });

  // Since promotions now appear on all pages, use all promotions
  const pagePromos = flattenedPromos.filter(
    (promo) => typeof promo.position === 'number'
  );

  // Get current window width to determine breakpoint (using getGlobalBreakpoints to avoid re-fetching)
  const getCurrentBreakpoint = () => {
    if (typeof window === 'undefined') {
      // Server-side fallback - use desktop as default
      return { isMobile: false, isTablet: false, isDesktop: true, isUltrawide: false };
    }

    const breakpoints = getGlobalBreakpoints();
    const width = window.innerWidth;

    if (width >= breakpoints.ultrawide) {
      return { isMobile: false, isTablet: false, isDesktop: false, isUltrawide: true };
    } else if (width > breakpoints.tablet) {
      return { isMobile: false, isTablet: false, isDesktop: true, isUltrawide: false };
    } else if (width > breakpoints.mobile) {
      return { isMobile: false, isTablet: true, isDesktop: false, isUltrawide: false };
    } else {
      return { isMobile: true, isTablet: false, isDesktop: false, isUltrawide: false };
    }
  };

  const currentBreakpoint = getCurrentBreakpoint();

  // Calculate total slots used by promotions, accounting for current responsive column spans
  let totalSlotsUsed = 0;

  pagePromos.forEach((promo) => {
    // Get the current responsive column span for this promotion (same logic as PromotionTile)
    const getCurrentColSpan = () => {
      let spanValue;
      if (currentBreakpoint.isUltrawide && promo.col_span_ultrawide) {
        spanValue = promo.col_span_ultrawide;
      } else if (currentBreakpoint.isDesktop && promo.col_span_desktop) {
        spanValue = promo.col_span_desktop;
      } else if (currentBreakpoint.isTablet && promo.col_span_tablet) {
        spanValue = promo.col_span_tablet;
      } else if (currentBreakpoint.isMobile && promo.col_span_mobile) {
        spanValue = promo.col_span_mobile;
      } else {
        spanValue = 1;
      }
      return spanValue;
    };

    // Get the current responsive row span for this promotion
    const getCurrentRowSpan = () => {
      let rowSpanValue;
      if (currentBreakpoint.isUltrawide && promo.row_span_ultrawide) {
        rowSpanValue = promo.row_span_ultrawide;
      } else if (currentBreakpoint.isDesktop && promo.row_span_desktop) {
        rowSpanValue = promo.row_span_desktop;
      } else if (currentBreakpoint.isTablet && promo.row_span_tablet) {
        rowSpanValue = promo.row_span_tablet;
      } else if (currentBreakpoint.isMobile && promo.row_span_mobile) {
        rowSpanValue = promo.row_span_mobile;
      } else {
        rowSpanValue = 1;
      }
      return Math.max(1, Math.min(2, rowSpanValue)); // Limit row spans to 1-2
    };

    // Get current responsive max columns
    const getCurrentMaxColumns = () => {
      if (currentBreakpoint.isUltrawide && columns?.ultrawide) return columns.ultrawide;
      if (currentBreakpoint.isDesktop && columns?.desktop) return columns.desktop;
      if (currentBreakpoint.isTablet && columns?.tablet) return columns.tablet;
      return columns?.mobile || 4;
    };

    const colSpan = getCurrentColSpan();
    const rowSpan = getCurrentRowSpan();
    const maxColumns = getCurrentMaxColumns();
    const effectiveColSpan = Math.max(1, Math.min(maxColumns, colSpan));

    // If the promotion spans the full width at the current breakpoint, don't count it
    // since it occupies its own row
    if (effectiveColSpan >= maxColumns) {
      // Full-row promotions don't count toward product slot reduction
      return;
    }

    // Add the column span multiplied by row span to account for total slots used
    // A 2x2 promo takes up 4 slots, a 1x2 promo takes up 2 slots, etc.
    totalSlotsUsed += effectiveColSpan * rowSpan;
  });

  return totalSlotsUsed;
}

function getSort(sort) {
  let defaultSort = 'Featured';
  switch (sort) {
    case 'created-ascending':
      defaultSort = 'Oldest';
      break;
    case 'created-descending':
      defaultSort = 'Newest';
      break;
    case 'best-selling':
      defaultSort = 'Popular';
      break;
    case 'title-ascending':
      defaultSort = 'TitleAZ';
      break;
    case 'title-descending':
      defaultSort = 'TitleZA';
      break;
    case 'manual':
      defaultSort = 'Featured';
      break;
    case 'price-descending':
      defaultSort = 'HighToLow';
      break;
    case 'price-ascending':
      defaultSort = 'LowToHigh';
      break;
    case 'Relevance':
      defaultSort = 'Relevance';
      break;
    default:
      defaultSort = 'Featured';
  }
  return defaultSort;
}

export const Collection = ({ settings, collectionProducts = [] }) => {
  // Get global search settings
  const { searchSettings, updateSearchSettings } = useSearchSettings();

  // Only render if settings.section and columns_mobile are defined
  if (!settings?.section || !settings.section.columns_mobile) {
    return <></>;
  }

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia(`(max-width: ${settings.globals?.responsive_breakpoints.desktop_min}px)`).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsMobile(isMobile);
      return;
    }

    const check = () => setIsMobile(window.matchMedia(`(max-width: ${settings.globals?.responsive_breakpoints.desktop_min}px)`).matches);

    // initialize and subscribe only to resize events
    check();
    window.addEventListener('resize', check);

    return () => {
      window.removeEventListener('resize', check);
    };
  }, [isMobile]);

  // Memoize extracted settings to prevent recreation on every render
  const sectionSettings = useMemo(
    () => ({
      columns_mobile: settings.section.columns_mobile,
      columns_tablet: settings.section.columns_tablet,
      columns_desktop: settings.section.columns_desktop,
      columns_ultrawide: settings.section.columns_ultrawide,
      column_gap_mobile: settings.section.column_gap_mobile,
      column_gap_tablet: settings.section.column_gap_tablet,
      column_gap_desktop: settings.section.column_gap_desktop,
      column_gap_ultrawide: settings.section.column_gap_ultrawide,
      row_gap_mobile: settings.section.row_gap_mobile,
      row_gap_tablet: settings.section.row_gap_tablet,
      row_gap_desktop: settings.section.row_gap_desktop,
      row_gap_ultrawide: settings.section.row_gap_ultrawide,
      id: settings.section.id,
      products_per_page: settings.section?.products_per_page || 12,
      pagination_type: settings.section?.pagination_type || 'load_more',
      default_sort: settings?.default_sort,
      toolbar_layout: settings.section?.toolbar_layout || 'default',
      product_count_position: settings.section?.product_count_position || 'center',
      sidebar_position: settings.section?.sidebar_position || 'left',
      show_filter_icon: settings.section?.show_filter_icon !== false,
      show_products_viewed_count: settings.section?.show_products_viewed_count !== false,
      show_sort_label: settings.section?.show_sort_label || false,
      filter_sort_direction: settings.section?.filter_sort_direction || 'normal',
      filter_width: settings.section?.filter_width || 480,
      are_filters_collapsible: settings.section?.are_filters_collapsible !== false,
      enable_filter_show_more: settings.section?.enable_filter_show_more !== false,
      filter_display_limit: settings.section?.filter_display_limit || 5,
      colour_swatches_use_images: settings.section?.colour_swatches_use_images !== false
    }),
    [settings.section]
  );

  // Memoized block settings
  const blockSettings = useMemo(
    () => ({
      filterButton: settings.filter_button_settings,
      sortDropdown: settings.sort_dropdown_settings,
      productTile: settings.product_tile_settings
    }),
    [settings.filter_button_settings, settings.sort_dropdown_settings, settings.product_tile_settings]
  );

  // Memoized collection info to prevent unnecessary re-renders
  const collectionInfo = useMemo(
    () => ({
      handle: settings.collection?.handle,
      id: settings.collection?.id,
      size: settings.collection_size
    }),
    [settings.collection?.handle, settings.collection?.id, settings.collection_size]
  );

  // Memoize the initialization dispatch to prevent re-firing
  const initializeSearch = useCallback(() => {
    const promoCount = getPromoCountForPage(
      settings.promotion_blocks || [],
      {
        desktop: sectionSettings.columns_desktop,
        tablet: sectionSettings.columns_tablet,
        mobile: sectionSettings.columns_mobile,
        ultrawide: sectionSettings.columns_ultrawide
      },
      sectionSettings.pagination_type
    );
    const adjustedProductsPerPage = sectionSettings.products_per_page - promoCount;

    // Get current page from URL or default to 1
    const urlParams = new URLSearchParams(window.location.search);
    const currentPageFromUrl = parseInt(urlParams.get('page') || '1', 10);

    window.dispatchEvent(
      new CustomEvent('searchrequest', {
        detail: {
          type: 'plp',
          collection: collectionInfo.handle,
          collectionId: collectionInfo.id,
          collectionSize: collectionInfo.size,
          filters_object: {},
          productsPerPage: adjustedProductsPerPage,
          page: currentPageFromUrl,
          paginationType: sectionSettings.pagination_type,
          sortOption: getSort(sectionSettings.default_sort),
          // For load_more pagination on page 2+, force sequential loading to show all previous pages
          forceSequentialLoad: sectionSettings.pagination_type === 'load_more' && currentPageFromUrl > 1
        }
      })
    );
  }, [sectionSettings, collectionInfo, settings.promotion_blocks]);

  useEffect(() => {
    // Update global search settings if not yet initialized
    if (!searchSettings.initialized) {
      updateSearchSettings({
        search_system: settings.search_system || 'shopify',
        settings: settings
      });
    }

    // Initialize the collection search when component mounts
    initializeSearch();


    // Save scroll position when navigating to a product
    const handleProductClick = () => {
      sessionStorage.setItem('collectionScrollPos', window.scrollY.toString());
      sessionStorage.setItem('collectionHandle', settings.collection?.handle);
      // Also save a timestamp to detect recent navigation
      sessionStorage.setItem('collectionScrollTimestamp', Date.now().toString());
    };

    const handlePageScroll = () => {
      // Check if we should restore scroll position
      const savedScrollPos = sessionStorage.getItem('collectionScrollPos');
      const savedHandle = sessionStorage.getItem('collectionHandle');
      const scrollTimestamp = sessionStorage.getItem('collectionScrollTimestamp');

      const isRecentNavigation = scrollTimestamp && (Date.now() - parseInt(scrollTimestamp)) < 600000;
      if (savedScrollPos && savedHandle === settings.collection?.handle && isRecentNavigation) {
        // Additional delay to ensure products are rendered
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedScrollPos, 10), behavior: 'smooth' });

          // Clean up after successful restoration
          sessionStorage.removeItem('collectionScrollPos');
          sessionStorage.removeItem('collectionHandle');
          sessionStorage.removeItem('collectionScrollTimestamp');
        }, 100);
      }
    };

    // Listen for products loaded events to trigger scroll restoration
    window.addEventListener('collection-products-loaded', handlePageScroll);

    // Add click handlers to product links
    const addProductClickHandlers = () => {
      const productLinks = document.querySelectorAll('.collection-tile a');
      productLinks.forEach((link) => {
        link.removeEventListener('click', handleProductClick);
        link.removeEventListener('keydown', handleProductClick);
        link.addEventListener('click', handleProductClick);
        link.addEventListener('keydown', handleProductClick);
      });
    };

    // Set up a mutation observer to watch for product tile changes
    const observer = new MutationObserver(() => {
      addProductClickHandlers();
    });

    // Start observing the product grid
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      observer.observe(productGrid, { childList: true, subtree: true });
    }

    return () => {
      // Clean up pageshow listener and DOM observer
      window.removeEventListener('collection-products-loaded', handlePageScroll);
      observer.disconnect();
    };
  }, [collectionInfo.handle, collectionInfo.size, searchSettings.initialized, updateSearchSettings, initializeSearch]);

  // Determine which search provider to use - use global settings if available
  const providerName = searchSettings.initialized ? searchSettings.search_system : settings.search_system || 'shopify';

  // Memoize the search provider to prevent recreation
  const SearchProviderComponent = useMemo(() => getSearchProvider(providerName), [providerName]);

  // Memoize the proxy component to prevent recreation
  const SearchProviderProxy = useMemo(() => {
    return (props) => <SearchProviderComponent {...props} />;
  }, [SearchProviderComponent]);

  // Memoize the columns object to prevent recreation
  const columns = useMemo(
    () => ({
      mobile: sectionSettings.columns_mobile,
      tablet: sectionSettings.columns_tablet,
      desktop: sectionSettings.columns_desktop,
      ultrawide: sectionSettings.columns_ultrawide,
      gapMobile: sectionSettings.column_gap_mobile,
      gapTablet: sectionSettings.column_gap_tablet,
      gapDesktop: sectionSettings.column_gap_desktop,
      gapUltrawide: sectionSettings.column_gap_ultrawide,
      rowGapMobile: sectionSettings.row_gap_mobile,
      rowGapTablet: sectionSettings.row_gap_tablet,
      rowGapDesktop: sectionSettings.row_gap_desktop,
      rowGapUltrawide: sectionSettings.row_gap_ultrawide
    }),
    [sectionSettings]
  );

  return (
    <CollectionProvider
      initialProducts={collectionProducts}
      defaultSettings={{
        collection: settings.collection,
        productsPerPage: sectionSettings.products_per_page,
        defaultSortOption: getSort(settings?.default_sort)
      }}
    >
      <SearchProviderProxy
        settings={searchSettings.initialized ? searchSettings.settings : settings}
        searchType='plp'
      >
        <CollectionWithProvider
          settings={settings}
          blockSettings={blockSettings}
          columns={columns}
          sectionSettings={sectionSettings}
          collectionInfo={collectionInfo}
        />
      </SearchProviderProxy>
    </CollectionProvider>
  );
};

// Internal component that uses the search provider context
function CollectionWithProvider({ settings, blockSettings, columns, sectionSettings, collectionInfo }) {
  const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();

  // Navigation loading state for event-driven spinner
  const [isNavigating, setIsNavigating] = useState(false);

  // Use a ref to store the sidebar toggle function to avoid re-renders
  const sidebarToggleRef = useRef<(() => void) | null>(null);

  const setSidebarToggle = useCallback((toggleFn: () => void) => {
    sidebarToggleRef.current = toggleFn;
  }, []);

  const toggleSidebar = useCallback(() => {
    if (sidebarToggleRef.current) {
      sidebarToggleRef.current();
    }
  }, []);

  // Access the search provider context
  const search = useSearch();

  // Access collection context
  const {
    products,
    filters,
    loading,
    totalResults,
    isLastPage,
    currentPage,
    loadMoreProducts,
    activeFilters,
    initialLoadComplete,
    sortOption
  } = useCollection();

    // Debug: Track renders
    const renderCount = useRef(0);
    renderCount.current += 1;

  // Set up event listeners for spinner events
  useEffect(() => {
    const handleShowSpinner = (event) => {
      if (event.detail.id === 'spinner-collection' || event.detail.id === 'spinner-search') {
        setIsNavigating(true);
      }
    };

    const handleHideSpinner = (event) => {
      if (event.detail.id === 'spinner-collection' || event.detail.id === 'spinner-search') {
        setIsNavigating(false);
      }
    };

    window.addEventListener('show-spinner', handleShowSpinner);
    window.addEventListener('hide-spinner', handleHideSpinner);

    return () => {
      window.removeEventListener('show-spinner', handleShowSpinner);
      window.removeEventListener('hide-spinner', handleHideSpinner);
    };
  }, []);

  // Debug logging for empty state
  // console.log('Collection Debug - loading:', loading, 'products.length:', products?.length || 0, 'totalResults:', totalResults, 'initialLoadComplete:', initialLoadComplete);

  // Calculate adjusted products per page (same calculation as in other functions)
  const adjustedProductsPerPage = sectionSettings.products_per_page - getPromoCountForPage(
    settings.promotion_blocks || [],
    {
      desktop: sectionSettings.columns_desktop,
      tablet: sectionSettings.columns_tablet,
      mobile: sectionSettings.columns_mobile,
      ultrawide: sectionSettings.columns_ultrawide
    }
  );

  // Calculate total pages for page-based pagination
  const totalPages = Math.ceil(totalResults / adjustedProductsPerPage);

  // Handler for page navigation - memoized to prevent recreation
  const goToPage = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
        // Dispatch show spinner event for navigation
        window.dispatchEvent(
          new CustomEvent('show-spinner', {
            detail: {
              id: 'spinner-collection',
              context: 'pagination-navigation'
            }
          })
        );

        // Update URL with new page parameter
        const url = new URL(window.location.href);
        url.searchParams.set('page', page);
        window.history.pushState({}, '', url);

        // Calculate promo count for this page
        const promoCount = getPromoCountForPage(
          settings.promotion_blocks || [],
          {
            desktop: sectionSettings.columns_desktop,
            tablet: sectionSettings.columns_tablet,
            mobile: sectionSettings.columns_mobile,
            ultrawide: sectionSettings.columns_ultrawide
          },
          sectionSettings.pagination_type
        );
        const adjustedProductsPerPage = sectionSettings.products_per_page - promoCount;

        // Handle different pagination types
        if (sectionSettings.pagination_type === 'load_more') {
          // For load more pagination, use searchrequest-nextpage to append products
          window.dispatchEvent(
            new CustomEvent('searchrequest', {
              detail: {
                type: 'plp',
                collection: collectionInfo.handle,
                collectionId: collectionInfo.id,
                collectionSize: collectionInfo.size,
                filters_object: activeFilters || {},
                productsPerPage: adjustedProductsPerPage,
                page: page,
                paginationType: sectionSettings.pagination_type,
                sortOption: getSort(sectionSettings.default_sort),
                skipUrlUpdate: true // We already updated the URL above
              }
            })
          );
        } else {
          // For page-based pagination, use regular searchrequest to replace products
          window.dispatchEvent(
            new CustomEvent('searchrequest', {
              detail: {
                type: 'plp',
                collection: collectionInfo.handle,
                collectionId: collectionInfo.id,
                collectionSize: collectionInfo.size,
                filters_object: activeFilters || {}, // Use activeFilters from collection context
                productsPerPage: adjustedProductsPerPage,
                page: page,
                paginationType: sectionSettings.pagination_type, // Add pagination type to help context decide how to handle products
                sortOption: getSort(sectionSettings.default_sort)
              }
            })
          );
        }

        // Scroll to top of results for page-based pagination, but not for load more
        if (sectionSettings.pagination_type !== 'load_more') {
          const productGrid = document.getElementById('product-grid');
          if (productGrid) {
            productGrid.scrollIntoView({ behavior: 'smooth' });
          }
        }

      }
    },
    [totalPages, currentPage, loading, sectionSettings, collectionInfo, settings.promotion_blocks, activeFilters]
  );

  // Memoize product tile features to prevent recalculation
  const productTileFeatures = useMemo(
    () => ({
      enableWishlist:
        blockSettings.productTile?.product_tile_wishlist_show || sectionSettings.product_tile_wishlist_show || true,
      showPrice:
        blockSettings.productTile?.product_tile_price_show !== false &&
        sectionSettings.product_tile_price_show !== false,
      showQuickAdd:
        blockSettings.productTile?.product_tile_quickadd_show || sectionSettings.product_tile_quickadd_show || true,
      showTags: blockSettings.productTile?.product_tile_tag_show || sectionSettings.product_tile_tag_show || true
    }),
    [blockSettings.productTile, sectionSettings]
  );

  // Calculate if this is the last page based on actual products loaded vs total results
  const isActuallyLastPage = totalResults <= (products?.length || 0) || (products?.length || 0) === 0;

  // Memoize stable settings for product rendering (exclude filter/sort related settings)
  const stableProductSettings = useMemo(
    () => ({
      // Only include settings that affect product tile rendering, not filtering/sorting
      product_tile_settings: settings.product_tile_settings,
      mobile_breakpoint: settings.mobile_breakpoint,
      collection: settings.collection,
      promotion_blocks: settings.promotion_blocks,
      // Add any other settings that ProductTile/PromotionTile actually need
      globals: settings.globals
    }),
    [
      settings.product_tile_settings,
      settings.mobile_breakpoint,
      settings.collection,
      settings.promotion_blocks,
      settings.globals
    ]
  );

  // Memoize the expensive promotion merging logic
  const mergedItems = useMemo(() => {
    type MergedItem = { type: 'promotion' | 'product'; data: any };
    let items: MergedItem[] = [];
    const productsCopy = products ? [...products] : [];

    // Check if any filters are active - if so, hide promotional tiles
    const hasActiveFilters = activeFilters && Object.keys(activeFilters).some(key => 
      activeFilters[key] && Array.isArray(activeFilters[key]) && activeFilters[key].length > 0
    );

    // If filters are active, only show products (no promotions)
    if (hasActiveFilters) {
      return productsCopy.map(product => ({ type: 'product' as const, data: product }));
    }

    // Flatten all promotions so each position is a separate entry
    // Show promotions on all pages with positions relative to each page (starting from 0)
    const promotions = (stableProductSettings.promotion_blocks || []).flatMap((promo) => {
      if (Array.isArray(promo.position)) {
        return promo.position.map((pos) => ({
          ...promo,
          position: pos,
          slotInPage: (pos - 1) % sectionSettings.products_per_page,
          pageForPosition: Math.floor((pos - 1) / sectionSettings.products_per_page) + 1
        }));
      }
      return [
        {
          ...promo,
          slotInPage: (promo.position - 1) % sectionSettings.products_per_page,
          pageForPosition: Math.floor((promo.position - 1) / sectionSettings.products_per_page) + 1
        }
      ];
    });

    // For load more pagination, include promotions from all loaded pages. For pages pagination, only current page.
    const pagePromotions = sectionSettings.pagination_type === 'load_more'
      ? promotions.filter((promo) => promo.pageForPosition <= currentPage) // All promotions up to current page
      : promotions.filter((promo) => promo.pageForPosition === currentPage); // Only current page promotions

    // Sort promotions by their position within the loaded content
    const sortedPromotions = pagePromotions.slice().sort((a, b) => a.position - b.position);

    const MAX_COLUMNS = 4;
    const MIN_COLUMNS = 1;
    const MAX_ROWS = 2;
    const MIN_ROWS = 1;

    // For load more pagination, show all products. For pages pagination, limit to products_per_page
    const maxItemsToShow = sectionSettings.pagination_type === 'load_more' ? productsCopy.length + pagePromotions.length : sectionSettings.products_per_page;

    let columnsUsed = 0;
    let rowsUsed = 0;
    let productIndex = 0;
    let promoIndex = 0;
    while (columnsUsed < maxItemsToShow && (productIndex < productsCopy.length || promoIndex < sortedPromotions.length)) {
      let added = false;

      // Insert promotion if its position matches the current grid slot
      while (promoIndex < sortedPromotions.length) {
        const promo = sortedPromotions[promoIndex];

        // For load more, use absolute position. For pages, use slotInPage relative to current page.
        const promoPosition = sectionSettings.pagination_type === 'load_more'
          ? promo.position - 1  // Convert to 0-based index
          : promo.slotInPage;

        if (promoPosition !== columnsUsed) {
          break; // This promotion doesn't belong at this position
        }

        // For now, use a simplified column span calculation
        // The responsive column/row spans will be handled by CSS classes in PromotionTile
        const colSpan = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, promo.column_span || 1));
        const rowSpan = Math.max(MIN_ROWS, Math.min(MAX_ROWS, promo.row_span || 1));

        // Check if promotion fits in current grid constraints
        if (columnsUsed + colSpan > maxItemsToShow) {
          break;
        }

        items.push({ type: 'promotion', data: promo });
        columnsUsed += colSpan;

        // Track the maximum rows used by this promotion
        // For multi-row promotions, we need to account for the additional vertical space
        if (rowSpan > 1) {
          rowsUsed = Math.max(rowsUsed, rowSpan);
        }

        promoIndex++;
        added = true;
      }
      // Insert product if available and space left
      if (productIndex < productsCopy.length && columnsUsed < maxItemsToShow) {
        items.push({ type: 'product', data: productsCopy[productIndex] });
        columnsUsed += 1;
        productIndex++;
        added = true;

        // Products typically use 1 row, but track it for consistency
        rowsUsed = Math.max(rowsUsed, 1);
      }
      // If nothing was added, break to avoid infinite loop
      if (!added) break;
    }

    return items;
  }, [products, stableProductSettings.promotion_blocks, sectionSettings.products_per_page, sectionSettings.pagination_type, currentPage, activeFilters]);

  // Memoize the actual product tiles (without container or loading states)
  const productTiles = useMemo(() => {
    return mergedItems.map((item) => {
      if (item.type === 'promotion') {
        return (
          <PromotionTile
            key={`promo-${JSON.stringify(item.data)}-${JSON.stringify(activeFilters)}-${sortOption}`}
            promotion={item.data}
            settings={item.data.settings}
            columns={columns}
            rows={{
              mobile: sectionSettings.row_gap_mobile,
              desktop: sectionSettings.row_gap_desktop
            }}
          />
        );
      } else if (item.type === 'product') {
        return (
          <div
            key={`product-${item.data.id}-${JSON.stringify(activeFilters)}-${sortOption}`}
            className={'collection-tile fade-in'}
          >
            <ProductTile
              settings={stableProductSettings}
              product={item.data}
              siblings={undefined}
              globals={{}}
              enableWishlist={productTileFeatures.enableWishlist}
              showPrice={productTileFeatures.showPrice}
              showQuickAdd={productTileFeatures.showQuickAdd}
              showTags={productTileFeatures.showTags}
            />
          </div>
        );
      }
    });
  }, [
    mergedItems,
    productTileFeatures.enableWishlist,
    productTileFeatures.showPrice,
    productTileFeatures.showQuickAdd,
    productTileFeatures.showTags,
    stableProductSettings.product_tile_settings,
    stableProductSettings.mobile_breakpoint,
    stableProductSettings.collection,
    stableProductSettings.promotion_blocks,
    stableProductSettings.globals
    // Only depend on actual product data and stable settings - not loading/UI states
  ]);

  // Memoize dynamic grid styles based on responsive breakpoints
  const gridStyles = useMemo(() => {
    let gridColumns = columns.mobile;

    if (isUltrawide && columns.ultrawide) {
      gridColumns = columns.ultrawide;
    } else if (isDesktop && columns.desktop) {
      gridColumns = columns.desktop;
    } else if (isTablet && columns.tablet) {
      gridColumns = columns.tablet;
    }

    let columnGap = columns.gapMobile;
    let rowGap = columns.rowGapMobile;

    if (isUltrawide && columns.gapUltrawide) {
      columnGap = columns.gapUltrawide;
    } else if (isDesktop && columns.gapDesktop) {
      columnGap = columns.gapDesktop;
    } else if (isTablet && columns.gapTablet) {
      columnGap = columns.gapTablet;
    }

    if (isUltrawide && columns.rowGapUltrawide) {
      rowGap = columns.rowGapUltrawide;
    } else if (isDesktop && columns.rowGapDesktop) {
      rowGap = columns.rowGapDesktop;
    } else if (isTablet && columns.rowGapTablet) {
      rowGap = columns.rowGapTablet;
    }

    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
      columnGap: `${columnGap}px`,
      rowGap: `${rowGap}px`,
      gridAutoColumns: 'max-content',
      gridAutoFlow: 'dense'
    };
  }, [isMobile, isTablet, isDesktop, isUltrawide, columns]);

  // Separate loading and empty state rendering outside of memoized grid
  const loadingAndEmptyStates = (
    <>
      {/* {loading && (products?.length || 0) === 0 && (
        <div className='col-span-full flex justify-center items-center py-8'>
          <LoadingSpinner id='spinner-collection' />
          {sectionSettings.pagination_type === 'pages' && currentPage > 1 && (
            <p className='ml-4 text-gray-600'>Loading page {currentPage}...</p>
          )}
        </div>
      )} */}

      {!loading && (products?.length || 0) === 0 && initialLoadComplete && (
        <div className='col-span-full text-center py-8'>
          <p className='text-lg mb-2'>No products found</p>
          <p className='text-gray-500'>Try adjusting your filters or search criteria</p>
        </div>
      )}

      {/* {loading && (products?.length || 0) > 0 && (
        <div className='col-span-full flex justify-center items-center py-8'>
          <div className='w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin'></div>
        </div>
      )} */}
    </>
  );

  return (
    <div
      data-search-provider={search.settings?.search_system}
      data-current-page={currentPage || 1}
      className='collection-results-container min-h-[600px]'
    >
      <CollectionSidebar
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
        setSidebarToggle={setSidebarToggle}
        sidebarPosition={sectionSettings.sidebar_position}
        sidebarWidth={sectionSettings.filter_width}
        show_filter_dividers={true}
        filter_divider_style={'1px solid #e5e7eb'}
        collapsible_filters={sectionSettings.are_filters_collapsible}
        enable_filter_show_more={sectionSettings.enable_filter_show_more}
        filter_display_limit={sectionSettings.filter_display_limit}
        colour_swatches_use_images={sectionSettings.colour_swatches_use_images}
      />

      <div className={`collection__toolbar relative flex justify-between z-10 ${sectionSettings.toolbar_layout === 'individual_filters' ? 'flex-col' : ''}`}>
        <CollectionToolbar
          settings={settings}
          filters={filters}
          toggleSidebar={toggleSidebar}
          filterButtonSettings={blockSettings.filterButton}
          sortDropdownSettings={blockSettings.sortDropdown}
          toolbarLayout={sectionSettings.toolbar_layout}
          productCountPosition={sectionSettings.product_count_position}
          showFilterIcon={sectionSettings.show_filter_icon}
          showSortLabel={sectionSettings.show_sort_label}
          showActiveFiltersInToolbar={sectionSettings.show_active_filters_in_toolbar}
          totalResults={totalResults}
          filterSortDirection={sectionSettings.filter_sort_direction}
          resultsTextSettings={settings.results_text_settings}
        />
      </div>

      {/* Product Grid Container */}
      <div
        id='product-grid'
        className='collection product-grid'
        data-id={`${sectionSettings.id}-collection-${collectionInfo.handle}`}
        style={{
          ...gridStyles,
          gridAutoRows: '1fr', // Make all rows equal height
          minHeight: 'min-content', // Ensure minimum content height is respected
        }}
      >
        {loadingAndEmptyStates}
        {(products?.length || 0) > 0 && productTiles}
      </div>

      {/* Product Count */}
      {((products?.length > 0) && sectionSettings.show_products_viewed_count) && (
        <div className='collection__product-count text-center mt-6'>
          {sectionSettings.pagination_type === 'pages' ? (
            <>
              Showing {(currentPage - 1) * sectionSettings.products_per_page + 1} to{' '}
              {Math.min(currentPage * sectionSettings.products_per_page, totalResults)} of {totalResults} products
              {totalPages > 1 && (
                <span className='ml-2'>
                  (Page {currentPage} of {totalPages})
                </span>
              )}
            </>
          ) : (
            <>
              {totalResults >= sectionSettings.products_per_page && (
                <span className={`${isMobile ? 'b2' : 'b4'}`}>Showing 1 - {products?.length || 0} of {totalResults}</span>
              )}
              {/* {currentPage > 1 && <span className='ml-2'>(Page {currentPage})</span>} */}
            </>
          )}
        </div>
      )}

      {/* Pagination / Load More */}
      {(products?.length || 0) > 0 && totalPages > 1 && !loading && (
        <div className='collection__pagination flex justify-center mt-8'>
          <ProductListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            goToPage={goToPage}
            loadMoreProducts={loadMoreProducts}
            paginationType={sectionSettings.pagination_type}
            isActuallyLastPage={isActuallyLastPage}
            paginationTextClass={settings.pagination_text_settings?.font_class || ''}
            paginationActiveColour={settings.pagination_text_settings?.active_colour || ''}
            paginationInactiveColour={settings.pagination_text_settings?.inactive_colour || ''}
          />
        </div>
      )}

      {/* Product Count */}
      {false && (products?.length || 0) > 0 && (
        <div className='collection__product-count text-center text-gray-500 mt-4'>
          {sectionSettings.pagination_type === 'pages' ? (
            <>
              Showing {(currentPage - 1) * sectionSettings.products_per_page + 1} to{' '}
              {Math.min(currentPage * sectionSettings.products_per_page, totalResults)} of {totalResults} products
              {totalPages > 1 && (
                <span className='ml-2'>
                  (Page {currentPage} of {totalPages})
                </span>
              )}
            </>
          ) : (
            <>
              Showing {products?.length || 0} of {totalResults} products
              {currentPage > 1 && <span className='ml-2'>(Page {currentPage})</span>}
            </>
          )}
        </div>
      )}

      {/* Loading spinner */}
      {isNavigating &&
        <Preloader
          animating={loading}
          showBackground={true}
          showLogo={true}
        />
      }
    </div>
  );
}
